/****************************************************************************
 * Copyright 2021 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/

import { useCallback, useEffect, useState } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'state';
import {
  selectEditor,
  selectEditorLayoutMode,
  selectIsSequenceEditInRNABuilderMode,
  selectKetcherId,
} from 'state/common';
import {
  LayoutMode,
  DEFAULT_LAYOUT_MODE,
  HAS_CONTENT_LAYOUT_MODE,
  ketcherProvider,
  KetcherLogger,
  Ketcher,
} from 'ketcher-core';

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export function useLayoutMode() {
  const ketcherId = useAppSelector(selectKetcherId);
  const editor = useAppSelector(selectEditor);
  const previousLayoutMode = useAppSelector(selectEditorLayoutMode);

  let ketcher: Ketcher;

  // TODO remove this try-catch and investigate why code execution comes here when ketcher instance is already removed
  //  This can happen when open/close several times the editor in duo mode
  try {
    ketcher = ketcherProvider.getKetcher(ketcherId);
  } catch (error) {
    KetcherLogger.error(
      `Failed to get ketcher instance with id ${ketcherId}`,
      error,
    );
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const isBlank = ketcher?.editor?.struct().isBlank();
  const fallbackMode = isBlank ? DEFAULT_LAYOUT_MODE : HAS_CONTENT_LAYOUT_MODE;
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(
    previousLayoutMode || fallbackMode,
  );
  const onLayoutModeChange = useCallback((newLayoutMode: LayoutMode) => {
    setLayoutMode(newLayoutMode);
  }, []);

  useEffect(() => {
    editor?.events.layoutModeChange.add(onLayoutModeChange);

    return () => {
      onLayoutModeChange(DEFAULT_LAYOUT_MODE);
      editor?.events.layoutModeChange.remove(onLayoutModeChange);
    };
  }, [onLayoutModeChange, editor]);

  return layoutMode;
}

export function useSequenceEditInRNABuilderMode() {
  const editor = useAppSelector(selectEditor);
  const isSequenceEditInRNABuilderModeInitial = useAppSelector(
    selectIsSequenceEditInRNABuilderMode,
  );
  const [isSequenceEditInRNABuilderMode, setIsSequenceEditInRNABuilderMode] =
    useState(isSequenceEditInRNABuilderModeInitial);

  const onSequenceEditInRNABuilderModeChange = useCallback((value: boolean) => {
    setIsSequenceEditInRNABuilderMode(value);
  }, []);

  useEffect(() => {
    editor?.events.toggleSequenceEditInRNABuilderMode.add(
      onSequenceEditInRNABuilderModeChange,
    );

    return () => {
      editor?.events.toggleSequenceEditInRNABuilderMode.remove(
        onSequenceEditInRNABuilderModeChange,
      );
    };
  }, [onSequenceEditInRNABuilderModeChange, editor]);

  return isSequenceEditInRNABuilderMode;
}
